import { useEffect, useRef, useState } from 'react';

/**
 * How long a registry lookup may run before the form offers to go on by hand. The server applies the
 * same rule (vendorhub-backend/src/modules/registration/kyc/lookupState.ts SLOW_LOOKUP_MS) and only
 * accepts a manual entry for the very lookup that is still running.
 */
export const SLOW_LOOKUP_MS = 1500;

/** True once `busy` has stayed true for SLOW_LOOKUP_MS; false again as soon as it ends. */
export function useSlowFlag(busy) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!busy) {
      setSlow(false);
      return undefined;
    }
    const id = setTimeout(() => setSlow(true), SLOW_LOOKUP_MS);
    return () => clearTimeout(id);
  }, [busy]);
  return slow;
}

/**
 * Lets a panel walk away from a lookup it started: the lookup's promise may still settle later, but
 * once `abandon()` was called its result is ignored (the vendor chose to type the detail in).
 * Usage: const run = lookup.start(); … if (lookup.isCurrent(run)) apply(result)
 */
export function useLookupTicket() {
  const ref = useRef(0);
  return {
    start: () => {
      ref.current += 1;
      return ref.current;
    },
    isCurrent: (ticket) => ticket === ref.current,
    abandon: () => {
      ref.current += 1;
    },
  };
}

/** Why a detail was typed in, in the vendor's words. */
export const MANUAL_REASON = {
  slow: 'the verification service was taking too long',
  timeout: 'the verification service timed out',
  upstream: 'the verification service was down',
  network: 'the verification service could not be reached',
  auth: 'the verification service refused our request',
  forbidden: 'automatic verification is not switched on for this account',
  unavailable: 'automatic verification is not switched on for this account',
  bad_response: 'the verification service answered wrongly',
  unknown: 'the verification service did not answer',
};
export const manualReasonText = (reason) => MANUAL_REASON[reason] || MANUAL_REASON.unknown;
